import type {
  CompanyBasicResponse,
  LeiChildItem,
  SubsidiaryCompanyItem,
} from '@/lib/backend-api';
import { localizePathname, type Locale } from '@/lib/i18n-routes';

/**
 * Merges LEI children (those with the GLEIF "IS_DIRECTLY_CONSOLIDATED_BY" edge)
 * into the Bundesanzeiger-derived subsidiaries list. Native subsidiaries are
 * all German; LEI children can be international, so we dedupe by normalized
 * country (defaulting to DE) + normalized name and prefer the native row when
 * both exist.
 *
 * Shared by the client detail view, the company SSR page's JSON-LD
 * subOrganization array, and the /api/companies/:id/dataset response.
 */
export function mergeSubsidiariesWithLei(
  subsidiaries: SubsidiaryCompanyItem[] | null | undefined,
  leiChildren: LeiChildItem[] | null | undefined,
  selfEuId?: string | null,
): SubsidiaryCompanyItem[] {
  const dedupeKey = (country: string | null | undefined, name: string) =>
    `${(country || 'DE').trim().toUpperCase()}|${name.trim().toLowerCase()}`;

  const natives = (subsidiaries ?? [])
    .filter((s) => !selfEuId || s.eu_id !== selfEuId)
    .map((s) => ({
      ...s,
      country_code: s.country_code ?? 'DE',
      source: s.source ?? ('bundesanzeiger' as const),
    }));

  const seen = new Set(natives.map((s) => dedupeKey(s.country_code, s.name)));
  const result: SubsidiaryCompanyItem[] = [...natives];

  for (const child of leiChildren ?? []) {
    if (child.relationship !== 'IS_DIRECTLY_CONSOLIDATED_BY') continue;
    if (!child.name) continue;
    if (selfEuId && child.eu_id && child.eu_id === selfEuId) continue;
    const key = dedupeKey(child.country_code, child.name);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push({
      eu_id: child.eu_id ?? null,
      name: child.name,
      sitz: child.city ?? null,
      country_code: child.country_code ?? null,
      register_id: child.register_id ?? null,
      register_type: child.register_type ?? null,
      register_court: child.register_court ?? null,
      source: 'lei',
      lei_code: child.lei_code ?? null,
    });
  }
  return result;
}


/**
 * Role codes that are NOT managers (e.g. Prokura, legal representatives).
 * Used to filter participants down to actual management (Geschäftsführer etc.)
 * in both the client-side company detail view and server-side SEO templates.
 */
/**
 * True when the participant's role denotes cap-table ownership (Kommanditist,
 * Aktionär, Anteilseigner, sole shareholder, etc.) as opposed to management.
 *
 * A *bare* "Gesellschafter"/"Gesellschafterin" is deliberately NOT treated as
 * ownership: in a Personengesellschaft register (OHG/KG) it denotes a
 * personally-liable managing partner, not a passive equity holder — the same
 * reason "persönlich haftend" is excluded. The actual equity owners come from
 * the Gesellschafterliste (GmbH/UG) or the CD Kommanditisten listing.
 *
 * Without this, a partner the register labels simply "Gesellschafterin" (e.g.
 * E. Merck KG, where one managing partner is recorded as "Gesellschafterin"
 * while her peers are "Persönlich haftender Gesellschafter") would be pulled
 * out of the management list and shown as the company's sole shareholder.
 */
export function isOwnerRole(role: string): boolean {
  if (/pers(ö|oe)nlich\s+haftend/i.test(role)) return false;
  return /kommanditist|komplementär|komplementaer|aktionär|aktionaer|anteilseigner|inhaber|alleingesellschafter/i.test(
    role,
  );
}

export const NON_MANAGER_ROLE_CODES = new Set([
  '002',
  '003',
  '004',
  '005',
  '019',
  '020',
  '021',
  '024',
  '027',
  '028',
  '029',
  '030',
  '031',
  '032',
  '033',
  '034',
  '036',
  '039',
  '044',
  '047',
  '050',
  '051',
  '056',
  '058',
  '059',
  '060',
  '061',
  '062',
  '071',
  '072',
  '074',
  '081',
  '082',
  '083',
  '089',
  '090',
  '092',
  '095',
  '096',
  '097',
  '099',
  '100',
  '101',
  '102',
  '103',
  '106',
  '108',
  '110',
  '115',
  '116',
  '117',
  '118',
  '121',
  '126',
  '128',
  '129',
  '130',
  '131',
  '135',
  '136',
  '137',
  '138',
  '139',
  '140',
  '141',
  '142',
  '143',
  '144',
  '146',
  '148',
  '149',
  '151',
  '153',
  '154',
  '156',
  '167',
  '174',
  '175',
  '176',
  '182',
  '187',
  '189',
  '190',
  '191',
  '197',
  '198',
  '199',
  '200',
  '202',
  '203',
  '204',
  '205',
  '208',
  '211',
  '217',
  '219',
  '224',
  '226',
  '230',
  '231',
  '232',
  '234',
  '235',
  '240',
  '241',
  '242',
  '245',
  '273',
  '288',
  '295',
  '296',
  '297',
  '301',
  '313',
  '316',
  '321',
  '324',
  '325',
  '334',
  '346',
  '347',
  '352',
  '353',
  '354',
  '355',
  '356',
  '357',
  '358',
  '359',
  '363',
  '367',
  '368',
  '369',
  '370',
  '371',
  '372',
  '373',
  '374',
  '375',
  '376',
  '377',
  '379',
  '381',
  '382',
  '385',
  '386',
  '387',
  '388',
  '389',
  '390',
  '391',
  '392',
  '397',
  '399',
  '287',
  '285',
]);

const UMLAUT_MAP: Record<string, string> = {
  ä: 'ae',
  ö: 'oe',
  ü: 'ue',
  ß: 'ss',
};

/** Swiss canton code (what `register_court` stores for CH-HR rows) → the
 *  canton's own official name (Vaud, not the German exonym Waadt), so the
 *  URL's court segment reads like a place ("vaud"), the way German URLs
 *  carry the Amtsgericht city ("stuttgart"). The backend's by-slug lookup
 *  translates the name slug back to the code
 *  (backend/zefix/utils.canton_code_for_slug). */
export const SWISS_CANTON_NAMES: Record<string, string> = {
  AG: 'Aargau',
  AI: 'Appenzell Innerrhoden',
  AR: 'Appenzell Ausserrhoden',
  BE: 'Bern',
  BL: 'Basel-Landschaft',
  BS: 'Basel-Stadt',
  FR: 'Fribourg',
  GE: 'Genève',
  GL: 'Glarus',
  GR: 'Graubünden',
  JU: 'Jura',
  LU: 'Luzern',
  NE: 'Neuchâtel',
  NW: 'Nidwalden',
  OW: 'Obwalden',
  SG: 'St. Gallen',
  SH: 'Schaffhausen',
  SO: 'Solothurn',
  SZ: 'Schwyz',
  TG: 'Thurgau',
  TI: 'Ticino',
  UR: 'Uri',
  VD: 'Vaud',
  VS: 'Valais',
  ZG: 'Zug',
  ZH: 'Zürich',
};

/** The two countries whose registers FirmenData covers. */
export type CompanyCountryCode = 'DE' | 'CH';

/** ISO-3166-1 alpha-2 country of a company row. Swiss companies are the
 *  CH-HR rows (eu_id "CH.…"); every other register type is German. The
 *  eu_id prefix covers callers that lack the register type. */
export function companyCountryCode(
  registerType?: string | null,
  euId?: string | null,
): CompanyCountryCode {
  return registerType === 'CH-HR' || euId?.startsWith('CH.') ? 'CH' : 'DE';
}

/**
 * A founding / first-register-entry date at the precision the register
 * actually knows it.
 *
 * A date on 1 January is the register's placeholder for "only the year is
 * known" — no court registers anything on New Year's Day, a public holiday —
 * so it comes back as the bare year ("1938"), which is also a valid ISO 8601
 * date and schema.org `Date`. Every other day comes back as ISO "YYYY-MM-DD".
 * Accepts ISO ("1938-01-01", with or without a time part), German
 * ("01.01.1938") and an already year-only ("1938") value; null when the input
 * is empty or unparseable.
 *
 * The one rule for every surface that shows such a date — the profile
 * tooltip, the history timeline, JSON-LD `foundingDate`, the dataset endpoint
 * and list exports. For German display, pass the result through
 * `isoToGerman` (a bare year passes through unchanged).
 */
export function foundingDateAtPrecision(
  raw: string | null | undefined,
): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (/^\d{4}$/.test(trimmed)) return trimmed;
  let iso: string | null = null;
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    iso = trimmed.slice(0, 10);
  } else {
    const german = trimmed.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (german) {
      const [, d, m, y] = german;
      iso = `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
  }
  if (!iso) return null;
  return iso.endsWith('-01-01') ? iso.slice(0, 4) : iso;
}

/**
 * Slugify a string: lowercase, replace umlauts, fold other diacritics to
 * their base letter (é→e, à→a — Swiss company names are full of them),
 * strip non-alphanumeric, collapse hyphens, trim leading/trailing hyphens.
 */
export function slugify(input: string): string {
  let s = input.toLowerCase();
  // Umlauts first — ä must become "ae", not the bare "a" NFD would leave.
  s = s.replace(/[äöüß]/g, (ch) => UMLAUT_MAP[ch] ?? ch);
  s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  s = s.replace(/[^a-z0-9]+/g, '-');
  s = s.replace(/-+/g, '-');
  s = s.replace(/^-|-$/g, '');
  return s;
}

/**
 * Generates a SEO-friendly slug for a company.
 *
 * Format: company/<court-slug>/<RegisterTypeRegisterId>/<name-slug>
 * Example: company/stuttgart/HRB19360/mercedes-benz-group-ag
 *
 * Always returns a fully localized absolute path (e.g. `/de/unternehmen/...`).
 */
export function generateCompanySlug(
  company: {
    name: string;
    sitz?: string | null;
    registerCourt?: string | null;
    registerType?: string | null;
    registerId?: string | null;
  },
  locale: Locale,
): string {
  const type = company.registerType || '';
  let court = (company.registerCourt || '').replace(/^Amtsgericht\s+/i, '');
  if (type === 'CH-HR') {
    // register_court holds the canton code ("VD") — spell out the canton so
    // the segment reads like a place, matching the German court-city style.
    court = SWISS_CANTON_NAMES[court.toUpperCase()] ?? court;
  }
  const courtSlug = slugify(court);

  const regId = (company.registerId || '').trim();
  // Swiss ids are self-identifying ("CHE-105.909.036") — gluing the CH-HR
  // register type in front would double up ("CH-HRCHE-…"), so the UID alone
  // is the segment: /unternehmen/waadt/CHE-105.909.036/nestle-s-a
  const typeId = type === 'CH-HR' ? regId : `${type}${regId}`;

  const nameSlug = slugify(company.name || '');

  const slug = `company/${courtSlug}/${typeId}/${nameSlug}`;

  return localizePathname(`/${slug}`, locale);
}

/**
 * Build a slug-form company URL from canonical register fields joined off the
 * `company` table (returned by backend endpoints as `canonical_*`). Returns
 * `null` when any required field is missing — callers degrade to an unlinked
 * label rather than emit a /company/<euId> link, which is the legacy shape
 * we are decommissioning.
 */
export function tryBuildCompanySlugFromCanonical(
  row: {
    canonical_name?: string | null;
    canonical_sitz?: string | null;
    canonical_register_court?: string | null;
    canonical_register_type?: string | null;
    canonical_register_id?: string | null;
  },
  locale: Locale,
): string | null {
  if (
    !row.canonical_name ||
    !row.canonical_register_court ||
    !row.canonical_register_type ||
    !row.canonical_register_id
  ) {
    return null;
  }
  return generateCompanySlug(
    {
      name: row.canonical_name,
      sitz: row.canonical_sitz ?? undefined,
      registerCourt: row.canonical_register_court,
      registerType: row.canonical_register_type,
      registerId: row.canonical_register_id,
    },
    locale,
  );
}

// ---------------------------------------------------------------------------
// SEO indexability — single source of truth shared by the SSR company page
// (generateMetadata robots tag + filtered hreflang) and the sitemap generation
// script. Changing this rule changes both surfaces in lockstep.
// ---------------------------------------------------------------------------

export interface CompanyIndexabilityInput {
  /** Company register type — only 'HRB' (GmbH/AG & similar) is indexable. */
  registerType?: string | null;
  isDeleted?: boolean | null;
  /** Has an AI company summary (yearly_metadata.firmen_beschreibung). */
  hasAiSummary?: boolean | null;
  /** Has at least one AI-extracted website_metadata record. */
  hasWebsiteMetadata?: boolean | null;
}

/**
 * Decide whether the company page should be indexed for the given locale.
 *
 * A page is indexable iff it is a non-deleted HRB company that carries unique,
 * AI-generated prose — i.e. an AI company summary (yearly_metadata) OR an
 * AI-extracted website summary (website_metadata). This is the single rule
 * shared by the SSR robots tag and the sitemap generator (kept in lockstep).
 *
 * EN exclusion: all English-locale pages are noindex'd (still crawlable). The
 * EN locale renders UI chrome in English but the company body (Gegenstand,
 * Eintragungen, manager roles, etc.) stays in German — Google treats it as
 * duplicate / auto-translated content and demotes at scale.
 */
export function isCompanyIndexable(
  input: CompanyIndexabilityInput,
  locale: Locale,
): boolean {
  if (locale === 'en') return false;
  if (input.isDeleted) return false;
  if (input.registerType !== 'HRB') return false;
  if (!input.hasAiSummary && !input.hasWebsiteMetadata) return false;
  return true;
}

/** Locales for which the given company page is indexable. */
export function indexableCompanyLocales(
  input: CompanyIndexabilityInput,
  supportedLocales: readonly Locale[],
): Locale[] {
  return supportedLocales.filter((loc) => isCompanyIndexable(input, loc));
}

/** Build an indexability input from a basic-endpoint response. */
export function companyIndexabilityFromBasic(
  company: CompanyBasicResponse,
): CompanyIndexabilityInput {
  return {
    registerType: company.register_type,
    isDeleted: company.is_deleted,
    hasAiSummary: !!company.ai_summary?.trim(),
    hasWebsiteMetadata: !!company.website_metadata,
  };
}
